import { existsSync } from 'node:fs';
import type { ParsedArgs } from './args.js';
import { booleanFlag, flag } from './args.js';
import type { CommandDefinition } from './catalog.js';
import { resolveCommandDefinition } from './catalog.js';
import { CliError, ExitCode } from './errors.js';

export function enforceCommandPolicy(args: ParsedArgs): CommandDefinition | undefined {
  const definition = resolveCommandDefinition(args.positionals);
  if (definition?.effect === 'local-write') {
    const output = flag(args.flags, 'output');
    if (!output?.trim()) {
      throw new CliError(`${definition.path} requires --output <path>`, ExitCode.Usage);
    }
    if (existsSync(output)) {
      throw new CliError(`Output path already exists: ${output}`, ExitCode.Usage, {
        kind: 'output-exists',
        path: output,
        command: definition.path,
        effect: definition.effect,
      });
    }
  }
  if (definition?.confirmation !== 'required') return definition;

  if (args.options.policy === 'read-only') {
    const commandKind = definition.effect === 'write' ? 'state-changing' : 'approval-required';
    throw new CliError(
      `Policy read-only blocks ${commandKind} command "${definition.path}"`,
      ExitCode.Usage,
      {
        kind: 'policy',
        policy: args.options.policy,
        command: definition.path,
        effect: definition.effect,
      },
    );
  }
  if (args.options.policy === 'interactive' && booleanFlag(args.flags, 'yes') !== true) {
    const reason = definition.effect === 'local-write' ? 'downloads private data' : 'changes state';
    throw new CliError(
      `${definition.path} ${reason}; rerun with --yes after approval`,
      ExitCode.Usage,
      {
        kind: 'confirmation-required',
        policy: args.options.policy,
        command: definition.path,
        effect: definition.effect,
      },
    );
  }
  return definition;
}

export function confirmationGranted(args: ParsedArgs, flagName = 'yes'): boolean {
  if (args.options.policy === 'unrestricted') return true;
  if (args.options.policy === 'read-only') return false;
  return booleanFlag(args.flags, flagName) === true || booleanFlag(args.flags, 'yes') === true;
}
